<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue';
import api from '@/api';
import Header from '@/components/layouts/header.vue';
import { UserDocumentContract } from '@/types';
import { useUserDocumentLimitNotification } from '@/utils/user-document/userDocumentLimitNotification';
import { useStore } from '@/utils/vue';

const store = useStore();

const hasUserDocumentNotification = computed(
  () => store.state.userDocumentNotification.hasNotification,
);
const userDocumentNotificationType = computed(
  () => store.state.userDocumentNotification.notificationType,
);
const { markAsSeen: markUserDocumentNotificationAsSeen } =
  useUserDocumentLimitNotification();

const activeContract = ref<UserDocumentContract | undefined>(undefined);
const activeContractLoading = ref(true);

const tabs = computed(() => {
  return [
    {
      name: 'Aconnectストレージ',
      linkTo: { name: 'anewsStorageIndex' },
    },
    {
      name: 'BOX',
      linkTo: { name: 'boxIndex' },
    },
    {
      name: 'SharePoint',
      linkTo: { name: 'sharepointIndex' },
    },
    {
      name: 'ストレージ容量',
      linkTo: { name: 'datasourceUsages' },
      hasNotification:
        hasUserDocumentNotification.value &&
        userDocumentNotificationType.value === 'usage_exceeded',
    },
  ];
});

onMounted(async () => {
  activeContract.value = await api
    .getTeamActiveUserDocumentContract()
    .catch(() => undefined)
    .finally(() => (activeContractLoading.value = false));
});

// 期限切れの場合、社内情報管理にアクセスすると閲覧済みにする
watch(
  userDocumentNotificationType,
  type => {
    if (type === 'contract_expired') {
      markUserDocumentNotificationAsSeen();
    }
  },
  { immediate: true },
);
</script>

<template>
  <div class="reset">
    <Header
      title="社内情報ストレージ管理"
      header-width="100%"
      :align-left="true"
      :tabs="tabs"
    >
      <template #additionalContent>
        <div
          class="no-contract-warning"
          v-if="!activeContract && !activeContractLoading"
        >
          社内情報契約が未設定、または期限切れのため連携できません。連携を再開したい場合、貴社の担当カスタマーサクセスもしくはAconnectサポートまでご連絡ください。
        </div>
      </template>
    </Header>
    <div class="user-document-container">
      <slot />
    </div>
  </div>
</template>

<style scoped lang="scss">
// 上位コンポーネントがマージンなどを管理しておりデザインが崩れるのでリセット
.reset {
  margin-bottom: -24px;
  padding: 0 !important;
}

.user-document-container {
  padding: 24px;
}

.no-contract-warning {
  color: $color-orange1000;
  font-size: 12px;
  margin-top: 10px;
}
</style>
